import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  BaseMessage,
  deliveryStatusMessage,
  incomingMessage,
  outgoingMessage,
} from "../../interfaces/messages.type";
import {
  SelectedConversaton,
  setSelectedConversation,
  updateConversationByConversationEvent,
  updateConversationsByIncomingMessage
} from "../../store/slices/conversation.slice";
import { decreasingReadCounterSmsDid, updateReadFlagSmsDid, updateSmsDidCounterByIncomingMessage } from "../../store/slices/SmsDids.slice";
import {
  onMessage,
  deleteMessage as deleteMessageSlice,
  updateDeliveryStatusSlice
} from "../../store/slices/messages.slice";
import {
  Conversation,
  UpdateConversation,
} from "../../interfaces/conversations.type";
import { useMessageToast } from "../../components/Layout/DefaultLayout";
import { useSocket } from "@data-phone/react-resource-server-auth";
import { deleteOfflineMessage, setOfflineMessage } from "../../store/slices/offline_messages.slice";
import useMessageNotification from "../notifications/messageNotifications";


export const useMessageSocket = () => {
  const socketRef: any = useRef<WebSocket | null>(null);
  const disconnectionToast = useRef<any>(null)
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const { selectedAccount } = useSelector((state: RootState) => state.admin)
  const { selectedDid } = useSelector((state: RootState) => state.smsDids)
  const { data: conversations, selectedConversation } = useSelector((state: RootState) => state.conversation)

  const selectedDidRef = useRef(selectedDid)
  const conversationRef = useRef<Conversation[]>([]);
  const selectedConversationRef = useRef<SelectedConversaton | null>(selectedConversation)
  const pendingConversationClientIds = useRef<Set<string>>(new Set());

  const { sendMessageNotification } = useMessageNotification();



  const dispatch: AppDispatch = useDispatch();
  const { messageApi } = useMessageToast();


  const { data: user } = useSelector(
    (state: RootState) => state.sessionData
  );

  const { socket, changeAccount } = useSocket({
    socketUrl: process.env.REACT_APP_SMS_MESSAGE_SOCKET_URL as string,
    path: process.env.REACT_APP_SMS_MESSAGE_SOCKET_PATH || "/messages/socket.io",
  })

  useEffect(() => {
    selectedDidRef.current = selectedDid
  }, [selectedDid])

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    changeAccount(selectedAccount);
  }, [selectedAccount])

  useEffect(() => {
    if (conversations && conversations.length > 0) {
      conversationRef.current = conversations
    }
  }, [conversations])

  useEffect(() => {
    selectedConversationRef.current = selectedConversation
  }, [selectedConversation])

  useEffect(() => {
    if (socket) {
      setIsSocketConnected(true);

      if (socket.connected) {
        console.log("Connected to message socket");
      }
      socket.on("message", handleMessageEvent);
      socket.on("conversation-update", handleUpdateConversation);
      socket.on("disconnect", handleDisconnectEvent);
      socket.io.on("reconnect", handleReconnectEvent);
      socket.io.on("reconnect_attempt", handleReconnectAttemptEvent);
      socket.io.on("reconnect_error", handleReconnectErrorEvent);
      socket.on("refresh-required", handleRefreshRequiredEvent);
      socket.on("message-delete", handleDeleteMessageEvent)
      socket.on("message-delivery-status", handleMessageDeliveryStatusEvent);

      socketRef.current = socket;
    }
    return () => {
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [socket]);

  const sendOutgoingMessage = (message: outgoingMessage) => {
    const recipients = message.to
      ? Array.isArray(message.to)
        ? message.to
        : [message.to]
      : selectedConversationRef.current?.participants || [];
    const baseMessage: BaseMessage = {
      clientId: message.clientId,
      conversation_id: message.conversation_id,
      did: message.from,
      sender_id: user?.id as string,
      participant: recipients[0] || "",
      conversation_participants: recipients,
      conversation_type: recipients.length > 1 ? "group" : "direct",
      body: message.text,
      media: message.media,
      direction: "outbound",
      timestamp: new Date().toISOString(),
      user_id: user?.id as string,
      deleted: false,
      first_name: user?.firstName ? user?.firstName : null,
      last_name: user?.lastName ? user?.lastName : null,
      status: "pending",
      delete_timestamp: null,
    };

    if (!message.conversation_id && message.clientId) {
      pendingConversationClientIds.current.add(message.clientId);
    }

    if (socketRef.current) {
      dispatch(onMessage(baseMessage));
      if (baseMessage.conversation_id) {
        dispatch(updateConversationsByIncomingMessage(baseMessage));
      }
      dispatch(deleteOfflineMessage(baseMessage.clientId as string))
      socketRef.current.emit("message", message);
      return true;
    }
    else {
      baseMessage.offlineMessage = true
      dispatch(setOfflineMessage(baseMessage))
      return false;
    }
  };



  const deleteMessage = (messageId: string) => {
    if (socketRef.current) {
      socketRef.current.emit("message-delete", messageId);
    }
  }

  const handleDeleteMessageEvent = (messageId: string) => {
    dispatch(deleteMessageSlice(messageId))
  }

  const handleMessageDeliveryStatusEvent = (message: deliveryStatusMessage) => {
    dispatch(updateDeliveryStatusSlice(message))
  }


  const handleMessageEvent = (message: incomingMessage) => {
    dispatch(updateSmsDidCounterByIncomingMessage({ ...message, user_id: user?.id }));
    const newMessage: BaseMessage = {
      ...message,
      user_id: user?.id as string,
      delete_timestamp: null,
      deleted: false,
      first_name: message.first_name || null,
      last_name: message.last_name || null,
    };
    dispatch(updateConversationsByIncomingMessage(newMessage));

    if (
      message.clientId &&
      pendingConversationClientIds.current.has(message.clientId)
    ) {
      pendingConversationClientIds.current.delete(message.clientId);
      const participants = message.conversation_participants?.length
        ? message.conversation_participants
        : [message.participant];
      dispatch(
        setSelectedConversation({
          conversation_id: message.conversation_id,
          did: message.did,
          participants,
          type:
            message.conversation_type ||
            (participants.length > 1 ? "group" : "direct"),
        })
      );
    } else if (selectedConversationRef.current?.conversation_id === message.conversation_id && message.direction === "inbound") {
      dispatch(setSelectedConversation({
        ...selectedConversationRef.current,
        total_unread: (selectedConversationRef.current.total_unread ?? 0) + 1
      }))
    }

    dispatch(onMessage(newMessage));
    if (message.direction == "inbound") {
      sendMessageNotification(message);
    }
  };


  const handleUpdateConversation = (conversation: UpdateConversation) => {
    dispatch(updateConversationByConversationEvent(conversation));

    if (Object.hasOwnProperty.call(conversation, 'total_read')) {
      dispatch(decreasingReadCounterSmsDid({ did: conversation.did, counter: Number(conversation.total_read) }));
    }

    if (Object.hasOwnProperty.call(conversation, 'unread')) {
      dispatch(updateReadFlagSmsDid({ did: conversation.did, unread: conversation.unread as boolean }));
    }
  };


  const handleDisconnectEvent = (error: any) => {

    console.log("Disconnected from message socket", error);

    if (disconnectionToast.current) {
      disconnectionToast.current.destroy();
    }

    if (window.location.pathname == "/") {
      messageApi.open({
        type: "loading",
        content: "Connection to message server lost - reconnecting",
        duration: 0,
      });
    }

    if (socketRef.current) {
      socketRef.current = null;
    }

    setIsSocketConnected(false);
  };

  const handleReconnectEvent = (attempt: number) => {
    console.log("Reconnected to message socket", attempt);


    messageApi.destroy();
    messageApi.success("Connected");

    socketRef.current = socket;
    setIsSocketConnected(true);
  };

  const handleReconnectAttemptEvent = (attempt: number) => {
    console.log("Attempting to reconnect to message socket", attempt);
  };

  const handleReconnectErrorEvent = (error: any) => {
    console.log("Error while reconnecting to message socket", error);
  };

  const handleRefreshRequiredEvent = (data: any) => {

    console.log("Refresh required from message socket", data);
  };

  return {
    sendOutgoingMessage,
    deleteMessage,
    isSocketConnected
  };
};
